export type UseContentEditableProps = InputProps & {
  onSubmit: (value: string) => void;
};

type InputProps = React.ComponentProps<'input'>;

const KEYBOARD_KEYS = {
  Escape: 'Escape',
  Enter: 'Enter',
  Backspace: 'Backspace',
};

type RulesKeys = 'maxLength';
type Handlers = {
  validate: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  transform: (content: string) => string;
};

type InputRules = (props: Pick<InputProps, RulesKeys>) => Record<RulesKeys, Handlers>;

const getInputRules: InputRules = (props) => ({
  maxLength: {
    validate: (event) => {
      if (!props.maxLength) return;

      const text = event.currentTarget.textContent || '';

      if (text.length >= props.maxLength && event.key !== KEYBOARD_KEYS.Backspace) {
        event.preventDefault();
      }
    },
    transform: (content) => {
      return content?.slice(0, props.maxLength);
    },
  },
});

export const useContentEditable = () => {
  let submitted = false;

  return ({ onSubmit, ...inputProps }: UseContentEditableProps): InputProps => {
    const rules = getInputRules(inputProps);

    const handleSubmit = (
      event: React.FocusEvent<HTMLInputElement, Element> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const input = event.currentTarget;
      const ruleKeys = Object.keys(rules) as RulesKeys[];
      const initialText = input.textContent || '';

      const text = ruleKeys.reduce((transformedText, rule) => {
        return inputProps[rule] ? rules[rule].transform(transformedText) : transformedText;
      }, initialText);

      if (text.length === 0) {
        // setTimeout is a workaround to make sure the focus is applied. Without it, it is not being applied in the right time
        setTimeout(() => input?.focus(), 0);
      } else {
        onSubmit(text);
        submitted = true;
        input.blur();
        submitted = false;
      }
    };

    const onBlur: InputProps['onBlur'] = (event) => {
      if (!submitted) handleSubmit(event);
    };

    const onKeyDown: InputProps['onKeyDown'] = (event) => {
      const ruleKeys = Object.keys(rules) as RulesKeys[];
      const input = event.currentTarget;

      ruleKeys.forEach((rule) => {
        if (inputProps[rule]) rules[rule].validate(event);
      });

      if (event.key === KEYBOARD_KEYS.Enter) {
        // prevents breaking the line
        event.preventDefault();
        event.stopPropagation();

        handleSubmit(event);
      } else if (event.key === KEYBOARD_KEYS.Escape) {
        event.preventDefault();
        event.stopPropagation();
        input.textContent = inputProps.defaultValue as string;
        input.blur();
      }
    };

    return {
      ...inputProps,
      contentEditable: true,
      suppressContentEditableWarning: true,
      tabIndex: 0,
      role: 'textbox',

      onBlur,
      onKeyDown,
    };
  };
};
